import request, { baseURL } from '@/utils/request';
import { errorMsg, warningMsg } from '@/utils/message';
import { errorNotify } from '@/utils/errorNotification';
import { ElNotification } from 'element-plus';
import { i18n } from '@/Lang/index';
import { getIndexThings } from '@/api/iot';
import { gwDevStatus, gwDevStatusMap, roleId, sysCfgType, topicType } from '@/utils/const';
import { publishTopic, getPubSubClientId, getTopic } from '@/utils/mqtt';
import { thingNamePrefix } from '@/utils/const';
import { getAreaList } from '@/api/area';
import { getSysCfg } from '@/api/sys';
import { adminCheck, getUserRole } from '@/api/user';
import { getGatewayServiceList, getGatewayService } from '@/api/gatewayService';
import { gatewayPermissionRequest, attachServiceId, requireService } from '@/utils/serviceContext';

/**
 * @param {Object} data 入力データオブジェクト
 * @param {string} data.area - エリアのID
 * @param {string} data.service - サービスのID
 * @return {Promise<Object[]>} テナントのゲートウェイ情報のリスト（サービス・ロールでフィルター済：ネットワーク設定情報を含む）
 */
export async function getGWList(data) {
  let res_devList = [];
  try {
    const devList = await getGWIdList({ area: data.area, service: data.service });
    await Promise.allSettled(
      devList.map(async (dev) => {
        dev.config = [];
        const res = await getSysCfg({ gateway: dev.devId, cfgKey: sysCfgType.subgCfg });
        if (res.status === 0 && res.data?.cfgValue) {
          dev.config = JSON.parse(res.data.cfgValue);
        }
        res_devList.push(dev);
      })
    );
  } catch {
    // TODO
    const res = {
      status: -2,
      data: [{ cause: errorMsg.e_request_failed_err }],
    };
    errorNotify(res);
  }
  const res_data = {
    count: res_devList.length,
    devList: res_devList,
  };
  return { status: 0, data: res_data };
}

/**
 * @param {Object} data 入力データオブジェクト
 * @param {string} data.area - エリアのID
 * @param {string} data.service - サービスのID
 * @return {Promise<Object[]>} テナントのゲートウェイのID関連情報のリスト（サービス・ロールでフィルター済）
 */
export async function getGWIdList(data) {
  let res_devList = [];
  try {
    const rawDevList = await getRawGWIdList({ area: data.area, service: data.service });
    const devList = rawDevList.filter((dev) => dev.deleteFlag !== true);
    const isAdmin = await adminCheck();
    await Promise.allSettled(
      devList.map(async (dev) => {
        const serviceList = await getGatewayServiceList({ gateway: dev.devId });
        if (serviceList.includes(data.service)) {
          dev.userRole = roleId.disabled;
          if (!isAdmin) {
            const role = await getUserRole({ gateway: dev.devId, serviceId: data.service });
            dev.userRole = role;
          } else {
            dev.userRole = roleId.admin;
          }
          if (dev.userRole !== roleId.disabled) {
            res_devList.push(dev);
          }
        }
      })
    );
  } catch {
    // TODO
    const res = {
      status: -2,
      data: [{ cause: errorMsg.e_request_failed_err }],
    };
    errorNotify(res);
  }
  return res_devList;
}

/**
 * @param {Object} data 入力データオブジェクト
 * @param {string} data.area - エリアのID
 * @param {string} data.service - サービスのID
 * @return {Promise<Object[]>} テナントのゲートウェイのID関連情報のリスト
 */
async function getRawGWIdList(data) {
  return await gatewayPermissionRequest(
    {
      url: '/areamasters/rwm/methods/gatewaylist',
      method: 'get',
      params: {
        area_id: data.area,
      },
    },
    {
      paramsRequireService: requireService({ serviceId: data.service }),
    }
  );
}

/**
 * @return {Promise<object[]>} テナントのゲートウェイのID関連情報のリスト
 */
export async function getTenantGWIdList() {
  let res_devList = [];
  try {
    const rawTenantGWList = await request({
      url: '/areamasters/rwm/methods/tenantgatewaylist',
      method: 'get',
    });
    const tenantGWList = rawTenantGWList.filter((dev) => dev.deleteFlag !== true);
    const areaList = (await getAreaList()).data.areaList;
    for (const gw of tenantGWList) {
      gw.areaName = areaList.find((area) => area.areaId === gw.areaId)?.name;
    }
    res_devList = tenantGWList;
  } catch {
    const res = {
      status: -2,
      data: [{ cause: errorMsg.e_request_failed_err }],
    };
    errorNotify(res);
  }
  return res_devList;
}

/*
export async function getGWInfo(data) {
  let gateway = null;
  try {
    gateway = await request({
      url: '/areamasters/rwm/methods/gateway',
      method: 'get',
      params: {
        gateway_id: data.gateway,
      },
    });
  } catch {
    const res = {
      status: -2,
      data: [{ cause: errorMsg.e_request_failed_err }],
    };
    errorNotify(res);
  }
  return gateway;
}
*/

/**
 * @param {Object} data 入力データオブジェクト
 * @param {string} data.gateway - ゲートウェイのID
 * @return {Promise<string>} ゲートウェイのステータス
 */
export async function getGWDevStatus(data) {
  let gwStatus = null;
  try {
    const gateway = await gatewayPermissionRequest({
      url: '/areamasters/rwm/methods/gateway',
      method: 'get',
      params: {
        gateway_id: data.gateway,
      },
    });
    if (gateway && gateway.status) {
      gwStatus = gateway.status;
    }
  } catch {
    // TODO
    const res = {
      status: -2,
      data: [{ cause: errorMsg.e_request_failed_err }],
    };
    errorNotify(res);
  }
  return gwStatus;
}

/**
 * @param {Object} data 入力データオブジェクト
 * @param {string} data.gateway - ゲートウェイのID
 * @param {string} data.status - ゲートウェイのステータス
 * @param {string} data.timestamp - 最終更新時刻
 * @return {Promise<Result>} 実行結果のオブジェクト（TODO:編集権限以上のみ実行可能）
 */
export async function setGWDevStatus(data) {
  let res = { status: 0 };
  try {
    await gatewayPermissionRequest({
      url: '/areamasters/rwm/methods/gatewaystatus',
      method: 'post',
      params: {
        gateway_id: data.gateway,
      },
      data: {
        status: data.status,
        lastDateTime: data.timestamp,
      },
    });
  } catch {
    // TODO
    res = {
      status: -2,
      data: [{ cause: errorMsg.e_request_failed_err }],
    };
    errorNotify(res);
  }
  return res;
}

// TODO: migrate to backend
/**
 * @param {Object} data 入力データオブジェクト
 * @param {string} data.tenant - テナントのID
 * @param {string} data.area - エリアのID
 * @param {{devId: string, status: string}[]} data.GWs - ゲートウェイ情報のリスト
 * @param {string} data.GWs[].devId - ゲートウェイのID
 * @param {string} data.GWs[].status - ゲートウェイのステータス
 * @return {Promise<object[]>} テナントのゲートウェイ情報のリスト（ネットワーク設定情報を含む）
 */
export async function updateGWDevsStatus(data) {
  const GWs = data.GWs;
  try {
    // cancel multiple check
    const payload = {
      clientId: getPubSubClientId(),
      timestamp: Date.now(),
    };
    const mqttData = {
      target: data.area,
      payload: payload,
    };
    const mqttTopic = getTopic(topicType.gatewayStatusChecked, {
      tenant: data.tenant,
      target: data.area,
    });
    publishTopic(mqttTopic, mqttData);
    const things = await getIndexThings({ tenant: data.tenant });
    // update status
    if (things.length > 0) {
      for (const GW of GWs) {
        for (const thing of things) {
          if (thing.thingName === `${thingNamePrefix}${GW.devId}`) {
            const connected = thing.connectivity.connected;
            const connectedStatus = connected ? gwDevStatus.normal : gwDevStatus.offline;
            const occupiedTimeout =
              GW.status === gwDevStatus.occupied && Math.abs(Date.now() - GW.lastDateTime) >= 5 * 60 * 1000;
            const connectivityChange =
              GW.status !== gwDevStatus.deactivated &&
              GW.status !== gwDevStatus.occupied &&
              connectedStatus !== GW.status;

            // console.log(GW.status);
            // console.log(`${GW.devId}: connectedStatus = ${connectedStatus}`);
            // console.log(`${GW.devId}: occupiedTimeout = ${occupiedTimeout}`);
            // console.log(`${GW.devId}: connectivityChange = ${connectivityChange}`);

            if (occupiedTimeout || connectivityChange) {
              try {
                const timestamp = Date.now();
                setGWDevStatus({
                  gateway: GW.devId,
                  status: connectedStatus,
                  timestamp: timestamp,
                });
                const payload = {
                  gateway: GW.devId,
                  timestamp: timestamp,
                  status: connectedStatus,
                };
                const mqttData = {
                  target: data.area,
                  payload: payload,
                };
                const mqttTopic = getTopic(topicType.gatewayStatusChange, {
                  tenant: data.tenant,
                  target: data.area,
                });
                publishTopic(mqttTopic, mqttData);

                // console.log(`${GW.devId}: Status Changed`);
              } catch {
                const res = {
                  status: -2,
                  data: [{ cause: errorMsg.e_request_failed_err }],
                };
                errorNotify(res);
              }
            }
            break;
          }
        }
      }
    }
  } catch {
    const res = {
      status: -2,
      data: [{ cause: errorMsg.e_request_failed_err }],
    };
    errorNotify(res);
  }
}

/**
 * @param {Object} data 入力データオブジェクト
 * @param {string} data.gateway - ゲートウェイのID
 * @return {Promise<boolean>} ゲートウェイのステータスが正常かそれ以外かの真偽値
 */
export async function gwDevStatusCheck(data) {
  const res_gwDevStatus = await getGWDevStatus({
    gateway: data.gateway,
  });
  if (res_gwDevStatus === gwDevStatus.normal) {
    return true;
  } else {
    let message = warningMsg.Unknown_Status;
    const status = gwDevStatusMap.get(res_gwDevStatus);
    if (status) {
      message = status;
    }
    ElNotification({
      title: i18n.global.t('gateway.warning'),
      message: message,
      type: 'warning',
      duration: 2000,
      customClass: 'notify',
    });
    return false;
  }
}

/**
 * @param {Object} data 入力データオブジェクト
 * @param {string} data.tenant - テナントのID
 * @param {string} data.area - エリアのID
 * @param {string} data.gateway - ゲートウェイのID
 * @param {boolean} data.occupied - ゲートウェイが占有状態かどうかの真偽値
 * @return {Promise<Result>} 実行結果のオブジェクト（TODO:編集権限以上のみ実行可能）
 */
export async function setGWDevOccupiedStatus(data) {
  let res = { status: 0 };
  try {
    const timestamp = Date.now();
    const status = data.occupied ? gwDevStatus.occupied : gwDevStatus.normal;
    const resData = await setGWDevStatus({
      gateway: data.gateway,
      status: status,
      timestamp: timestamp,
    });
    if (resData.status === 0) {
      const payload = {
        gateway: data.gateway,
        timestamp: timestamp,
        status: status,
      };
      const mqttData = {
        target: data.area,
        payload: payload,
      };
      const mqttTopic = getTopic(topicType.gatewayStatusChange, {
        tenant: data.tenant,
        target: data.area,
      });
      publishTopic(mqttTopic, mqttData);
    } else {
      res = resData;
    }
  } catch {
    res = {
      status: -2,
      data: [{ cause: errorMsg.e_request_failed_err }],
    };
    errorNotify(res);
  }
  return res;
}

/**
 * @param {Object} data 入力データオブジェクト
 * @param {string} data.gateway - ゲートウェイのID
 * @param {string} data.name - ゲートウェイの名前
 * @return {Promise<Result>} 実行結果のオブジェクト（TODO:編集権限以上のみ実行可能）
 */
export async function editDevName(data) {
  let res = { status: 0 };
  try {
    await gatewayPermissionRequest({
      url: '/areamasters/rwm/methods/gatewayname',
      method: 'post',
      params: {
        gateway_id: data.gateway,
      },
      data: {
        name: data.name,
      },
    });
  } catch {
    // TODO
    res = {
      status: -2,
      data: [{ cause: errorMsg.e_request_failed_err }],
    };
    errorNotify(res);
  }
  return res;
}

/**
 * @param {Object} data 入力データオブジェクト
 * @param {string} data.tenant - テナントのID
 * @param {string} data.area - エリアのID
 * @param {string} data.gateway - ゲートウェイのID
 * @param {boolean} data.occupied - ゲートウェイが占有状態かどうかの真偽値
 * @param {string} data.token - API実行用のトークン
 * @return {Promise<void>}
 */
export function setGWDevStatusKeepAlive(data) {
  try {
    const timestamp = Date.now();
    const status = data.occupied ? gwDevStatus.occupied : gwDevStatus.normal;
    const params = attachServiceId({
      area_id: data.area,
      gateway_id: data.gateway,
    });
    const gatewayData = { status: status, lastDateTime: timestamp };
    const urlParams = new URLSearchParams(params);
    const url = baseURL + '/areamasters/rwm/methods/gateway' + '?' + urlParams.toString();
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${data.token}`,
      },
      body: JSON.stringify(gatewayData),
      keepalive: true,
    });
    const payload = {
      gateway: data.gateway,
      timestamp: timestamp,
      status: status,
    };
    const mqttData = {
      target: data.area,
      payload: payload,
    };
    const mqttTopic = getTopic(topicType.gatewayStatusChange, {
      tenant: data.tenant,
      target: data.area,
    });
    publishTopic(mqttTopic, mqttData);
  } catch {
    // TODO
    const res = {
      status: -2,
      data: [{ cause: errorMsg.e_request_failed_err }],
    };
    errorNotify(res);
  }
}

/**
 * @param {Object} data 入力データオブジェクト
 * @param {string} data.gatewayId - ゲートウェイのID
 * @return {Promise<Boolean>} レスポンス
 */
export async function gatewayDuplicateCheck(data) {
  let gatewayDuplicated = false;
  const gatewayDuplicateNotify = () => {
    ElNotification({
      title: i18n.global.t('gateway.warning'),
      message: warningMsg.Duplicated_Gateway_Id,
      type: 'warning',
      duration: 2000,
      customClass: 'notify',
    });
  };
  try {
    const gateway = await getGatewayService({ gatewayId: data.gatewayId });
    if (gateway != null && gateway.gatewayId === data.gatewayId) {
      gatewayDuplicated = true;
    }
    if (gatewayDuplicated) {
      gatewayDuplicateNotify();
    }
  } catch {
    gatewayDuplicated = true;
    errorNotify({ status: -2 });
  }
  return gatewayDuplicated;
}
